import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, HelpCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { AIPracticeDialog } from "./AIPracticeDialog";
import { useAIQuota } from "@/hooks/useAIQuota";

interface QuizOption {
  id: string;
  text: string;
}

interface InlineQuizProps {
  quizId: string;
  question: string;
  options: QuizOption[];
  correctAnswer: string;
  explanation: string | null;
  difficulty: string;
  category?: string;
  moduleTitle?: string;
  onAnswered?: (isCorrect: boolean) => void;
}

export function InlineQuiz({
  question,
  options,
  correctAnswer,
  explanation,
  difficulty,
  category,
  moduleTitle,
  onAnswered,
}: InlineQuizProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [hasAnswered, setHasAnswered] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const { quiz_remaining, quiz_limit, isUnlimited } = useAIQuota();
  const isCorrect = selectedAnswer === correctAnswer;

  const difficultyLabels: Record<string, string> = {
    easy: "Fácil",
    medium: "Médio",
    hard: "Difícil",
  };

  const difficultyColors: Record<string, string> = {
    easy: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    medium: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    hard: "bg-red-500/10 text-red-600 border-red-500/20",
  };

  const handleSubmit = () => {
    if (!selectedAnswer) return;
    setHasAnswered(true);
    onAnswered?.(selectedAnswer === correctAnswer);
  };

  // Texto da opção correta para passar de contexto pra IA
  const correctOptionText =
    options.find((o) => o.id === correctAnswer)?.text ?? correctAnswer;

  const showPracticeButton = !!category && !!moduleTitle;
  const remainingLabel = isUnlimited
    ? "∞"
    : `${quiz_remaining}/${quiz_limit}`;

  return (
    <>
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Exercício</CardTitle>
            <Badge variant="outline" className={cn("text-[10px]", difficultyColors[difficulty])}>
              {difficultyLabels[difficulty] || difficulty}
            </Badge>
          </div>
          <p className="text-foreground font-medium">{question}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={selectedAnswer}
            onValueChange={(v) => !hasAnswered && setSelectedAnswer(v)}
            className="space-y-2"
          >
            {options.map((option) => {
              const isThis = selectedAnswer === option.id;
              const isRight = option.id === correctAnswer;
              let optionClass = "border-border";
              if (hasAnswered) {
                if (isRight) optionClass = "border-emerald-500 bg-emerald-500/5";
                else if (isThis && !isCorrect) optionClass = "border-red-500 bg-red-500/5";
              }
              return (
                <Label
                  key={option.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                    !hasAnswered && "hover:bg-accent",
                    hasAnswered && "cursor-default",
                    optionClass
                  )}
                >
                  <RadioGroupItem value={option.id} disabled={hasAnswered} />
                  <span className="text-sm flex-1">{option.text}</span>
                  {hasAnswered && isRight && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  {hasAnswered && isThis && !isCorrect && <XCircle className="h-4 w-4 text-red-500" />}
                </Label>
              );
            })}
          </RadioGroup>

          {!hasAnswered && (
            <Button onClick={handleSubmit} disabled={!selectedAnswer} className="w-full">
              Confirmar Resposta
            </Button>
          )}

          {hasAnswered && (
            <div
              className={cn(
                "p-4 rounded-lg border",
                isCorrect ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                {isCorrect ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <span className="font-semibold text-sm">
                  {isCorrect ? "Correto! 🎉" : "Incorreto"}
                </span>
              </div>
              {explanation && (
                <p className="text-sm text-muted-foreground mt-1">{explanation}</p>
              )}

              {showPracticeButton && (
                <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">
                    Quer treinar mais esse conceito?
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPracticeOpen(true)}
                    className="gap-1.5"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Treinar mais com IA
                    <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
                      {remainingLabel}
                    </Badge>
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showPracticeButton && (
        <AIPracticeDialog
          open={practiceOpen}
          onOpenChange={setPracticeOpen}
          context={{
            moduleTitle: moduleTitle!,
            category: category!,
            originalQuestion: question,
            originalCorrectAnswer: correctOptionText,
            originalExplanation: explanation,
            difficulty,
          }}
        />
      )}
    </>
  );
}
